import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import {
  NumberFieldControl,
  SelectFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'

import type { CreateTableBody, PosBranchRef, PosTable, PosZone, UpdateTableBody } from '../types'
import { EDITABLE_TABLE_STATES, TableState } from '../types'
import { branchLabel, tableStateStyles } from './pos-format'

/** Sentinel for "no zone" — React Aria keys a Select by id, and null is not one. */
const UNZONED = '__unzoned__'

const schema = z.object({
  branchId: z.string().min(1, 'Choose an outlet.'),
  code: z.string().trim().min(1, 'Give the table a code.').max(24),
  zoneId: z.string().optional(),
  seats: z.number({ message: 'How many does it seat?' }).int().min(1).max(99),
  status: z.string().optional(),
})

type TableFormValues = z.infer<typeof schema>

interface TableFormDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Null creates. */
  table: PosTable | null
  branches: PosBranchRef[]
  zones: PosZone[]
  isSubmitting: boolean
  error?: unknown
  onCreate: (body: CreateTableBody) => Promise<unknown>
  onUpdate: (id: string, body: UpdateTableBody) => Promise<unknown>
}

/**
 * Add or edit one table.
 *
 * The outlet is fixed once a table exists: its QR is printed and stuck to it,
 * and a code is only unique within a room. Moving a table between branches
 * means retiring it here and adding it there — which is also what physically
 * happened.
 *
 * The status field offers only Available and Out of service. Occupied, ordered,
 * served and dirty belong to the till, which can see the room; the API rejects
 * them from this side, so the form must not offer them.
 */
export function TableFormDialog({
  isOpen,
  onOpenChange,
  table,
  branches,
  zones,
  isSubmitting,
  error,
  onCreate,
  onUpdate,
}: TableFormDialogProps) {
  const isEditing = Boolean(table)

  const { control, handleSubmit } = useForm<TableFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      branchId: table?.branchId ?? branches[0]?.id ?? '',
      code: table?.code ?? '',
      zoneId: table?.zoneId ?? UNZONED,
      seats: table?.seats ?? 2,
      status: table?.status ?? TableState.Available,
    },
  })

  // `useWatch` rather than the form's `watch()`: the latter returns a fresh
  // function on every render, which opts this component out of memoisation
  // entirely for the sake of reading one field.
  const branchId = useWatch({ control, name: 'branchId' })

  // A table cannot stand in a room at another outlet, so the picker narrows
  // with the branch above it rather than offering zones the API would refuse.
  const zoneOptions = [
    { value: UNZONED, label: 'Unzoned' },
    ...zones
      .filter((zone) => zone.branchId === branchId)
      .map((zone) => ({ value: zone.id, label: zone.name })),
  ]

  async function submit(values: TableFormValues) {
    const zoneId = values.zoneId === UNZONED ? null : (values.zoneId ?? null)

    if (table) {
      await onUpdate(table.id, {
        code: values.code.trim(),
        zoneId,
        seats: values.seats,
        status: values.status as TableState,
      })
    } else {
      await onCreate({
        branchId: values.branchId,
        code: values.code.trim(),
        zoneId: zoneId ?? undefined,
        seats: values.seats,
      })
    }

    onOpenChange(false)
  }

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEditing ? `Edit ${table?.code}` : 'Add a table'}
      description={
        isEditing
          ? 'A table keeps its outlet — its QR is stuck to it, and a code is only unique within a room.'
          : 'Its QR is minted at the same time, so the table is scannable straight away.'
      }
      submitLabel={isEditing ? 'Save table' : 'Add table'}
      onSubmit={handleSubmit(submit)}
      isBusy={isSubmitting}
      error={error}
      size="sm"
    >
      <SelectFieldControl
        control={control}
        name="branchId"
        label="Outlet"
        isRequired
        isDisabled={isEditing}
        options={branches.map((branch) => ({
          value: branch.id,
          label: branchLabel(branch),
        }))}
      />

      <TextFieldControl
        control={control}
        name="code"
        label="Code"
        placeholder="A1"
        description="What is written on the table. Unique within its outlet."
        isRequired
      />

      <SelectFieldControl
        control={control}
        name="zoneId"
        label="Zone"
        options={zoneOptions}
        description="Ungrouped tables still appear on the floor, under Unzoned."
      />

      <NumberFieldControl
        control={control}
        name="seats"
        label="Seats"
        minValue={1}
        maxValue={99}
        step={1}
        description="Capacity, not the party currently at it."
        isRequired
      />

      {isEditing && (
        <SelectFieldControl
          control={control}
          name="status"
          label="Status"
          options={EDITABLE_TABLE_STATES.map((state) => ({
            value: state,
            label: tableStateStyles[state].label,
          }))}
          description="Occupied, ordered, served and dirty are set by the till, which can see the room."
        />
      )}
    </FormModal>
  )
}
